import type { FieldValues, UseFormSetError } from "react-hook-form";

import { resolveServerErrors } from "helpers/requestFormHelper";
import { mapClassicScheduleErrorsToStops } from "@RequestFormV2/utils/mapClassicScheduleErrors";
import { mapPackingListErrorsToFormRows } from "@RequestFormV2/utils/mapPackingListErrorsToFormRows";

/** Error type used by `resolveServerErrors` and this helper for API errors. */
export const SERVER_ERROR_TYPE = "serverError";

type ApiFieldError = { path: string; message: string };

function collectApiFieldErrors(value: unknown, path: string): ApiFieldError[] {
  if (typeof value === "string") return [{ path, message: value }];

  if (Array.isArray(value)) {
    // DRF-style ["msg"] is the field's messages; any other array holds nested rows such as schedule stops.
    if (value.length > 0 && value.every((item) => typeof item === "string")) {
      return [{ path, message: value[0] }];
    }
    return value.flatMap((item, index) => collectApiFieldErrors(item, `${path}.${index}`));
  }

  if (value && typeof value === "object") {
    return Object.entries(value).flatMap(([key, child]) => collectApiFieldErrors(child, path ? `${path}.${key}` : key));
  }

  return [];
}

function hasPath(values: unknown, path: string): boolean {
  let current = values;
  return path.split(".").every((segment) => {
    if (!current || typeof current !== "object" || !Object.prototype.hasOwnProperty.call(current, segment)) {
      return false;
    }
    current = (current as Record<string, unknown>)[segment];
    return true;
  });
}

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

/**
 * The Quick draft API sends `shipment.title` / `shipment.comments` as `quick_request.*`
 * (see `prepareDraftPayload`), so its errors come back under `quick_request`.
 */
function mapQuickRequestErrorsToShipment(data: Record<string, unknown>): Record<string, unknown> {
  const { quick_request: quickRequest, ...rest } = data;
  if (!isPlainObject(quickRequest)) return data;
  if (rest.shipment !== undefined && !isPlainObject(rest.shipment)) return data;

  return { ...rest, shipment: { ...(rest.shipment as Record<string, unknown> | undefined), ...quickRequest } };
}

/**
 * Sets draft API errors on the matching form fields.
 * `resolveServerErrors` (shared with V1) covers `section.field`; nested paths such as
 * `schedule.stops.0.stop_contact_email` are set here, only when the field exists in the form.
 * Quick Request classic schedule errors (`pickup_*` / `stop1_*`) are mapped back onto their stops.
 */
export function applyApiFieldErrors(error: unknown, values: FieldValues, setError: UseFormSetError<FieldValues>) {
  const apiError = error as { response?: { data?: unknown } } | undefined;
  const responseData = apiError?.response?.data;
  if (!isPlainObject(responseData)) {
    resolveServerErrors(error, { ...values }, setError);
    return;
  }

  const data = mapPackingListErrorsToFormRows(mapQuickRequestErrorsToShipment(responseData), values.packing_list);
  resolveServerErrors({ ...apiError, response: { ...apiError?.response, data } }, { ...values }, setError);

  const nestedErrors = collectApiFieldErrors(data, "").filter(
    ({ path }) => path.split(".").length > 2 && hasPath(values, path),
  );
  // Only produced for stops that exist, and may target fields a stop has no value for yet (e.g. address_id).
  const classicScheduleErrors = mapClassicScheduleErrorsToStops(data.schedule, values.schedule?.stops);

  [...nestedErrors, ...classicScheduleErrors].forEach(({ path, message }) =>
    setError(path, { type: SERVER_ERROR_TYPE, message }),
  );
}
